import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraICTServices,
  setDoraICTServices,
} from '../store/ictService/ictServiceSlice.ts';
import {
  addDoraTestingExercise,
  removeDoraTestingExercise,
  selectDoraTestingExercises,
  setDoraTestingExercises,
  updateDoraTestingExercise as updateDoraTestingExerciseState,
} from '../store/testingExercise/testingExerciseSlice.ts';
import {
  listDoraICTServices,
  listDoraTestingExercises,
  createDoraTestingExercise,
  updateDoraTestingExercise,
  deleteDoraTestingExercise,
} from '../../../api.ts';
import { DoraTestingExercise, doraTestingExerciseToUpdate } from '../models/doraTestingExercise.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

const EXERCISE_TYPES = ['pen_test', 'tlpt', 'bcp_test', 'dr_test'];

export const TestingExercisesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const ictServices = useAppSelector(selectDoraICTServices);
  const exercises = useAppSelector(selectDoraTestingExercises);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [executedAt, setExecutedAt] = useState('');
  const [exerciseType, setExerciseType] = useState<string>('pen_test');
  const [ictServiceId, setICTServiceId] = useState<number | ''>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editExecutedAt, setEditExecutedAt] = useState('');
  const [editExerciseType, setEditExerciseType] = useState<string>('pen_test');
  const [editICTServiceId, setEditICTServiceId] = useState<number | ''>('');

  const sortedExercises = useMemo(
    () => [...exercises].sort((a, b) => a.name.localeCompare(b.name)),
    [exercises],
  );

  useEffect(() => {
    if (!executedAt) {
      setExecutedAt(new Date().toISOString().slice(0, 16));
    }
  }, [executedAt]);

  const createDisabled = !name.trim();

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [icts, list] = await Promise.all([
          listDoraICTServices(),
          listDoraTestingExercises(),
        ]);
        dispatch(setDoraICTServices(icts));
        dispatch(setDoraTestingExercises(list));
      } catch (err: any) {
        console.error('Failed to load testing exercises', err);
        setError(err?.response?.data?.detail || 'Failed to load testing exercises');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraTestingExercise({
        name: name.trim(),
        description: description || null,
        executed_at: executedAt ? new Date(executedAt).toISOString() : null,
        exercise_type: exerciseType,
        ict_service_id: ictServiceId === '' ? null : Number(ictServiceId),
      });
      dispatch(addDoraTestingExercise(created));
      setName('');
      setDescription('');
      setExecutedAt('');
      setExerciseType('pen_test');
      setICTServiceId('');
      setSuccess('Testing exercise created');
    } catch (err: any) {
      console.error('Failed to create testing exercise', err);
      setError(err?.response?.data?.detail || 'Failed to create testing exercise');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (exercise: DoraTestingExercise) => {
    setEditingId(exercise.id ?? null);
    setEditName(exercise.name);
    setEditDescription(exercise.description || '');
    setEditExecutedAt(exercise.executed_at ? exercise.executed_at.slice(0, 16) : '');
    setEditExerciseType(exercise.exercise_type || 'pen_test');
    setEditICTServiceId(exercise.ict_service_id ?? '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditExecutedAt('');
    setEditExerciseType('pen_test');
    setEditICTServiceId('');
  };

  const handleUpdate = async (exercise: DoraTestingExercise) => {
    if (!exercise.id) return;
    if (!editName.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraTestingExerciseToUpdate({
        ...exercise,
        name: editName.trim(),
        description: editDescription || null,
        executed_at: editExecutedAt ? new Date(editExecutedAt).toISOString() : exercise.executed_at,
        exercise_type: editExerciseType,
        ict_service_id: editICTServiceId === '' ? null : Number(editICTServiceId),
      });
      const updated = await updateDoraTestingExercise(exercise.id, payload);
      dispatch(updateDoraTestingExerciseState(updated));
      cancelEdit();
      setSuccess('Testing exercise updated');
    } catch (err: any) {
      console.error('Failed to update testing exercise', err);
      setError(err?.response?.data?.detail || 'Failed to update testing exercise');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this testing exercise?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraTestingExercise(id);
      dispatch(removeDoraTestingExercise(id));
      setSuccess('Testing exercise deleted');
    } catch (err: any) {
      console.error('Failed to delete testing exercise', err);
      setError(err?.response?.data?.detail || 'Failed to delete testing exercise');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Testing Exercise
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="exercise-type-label">Exercise Type</InputLabel>
              <Select
                labelId="exercise-type-label"
                value={exerciseType}
                label="Exercise Type"
                onChange={(e: SelectChangeEvent<string>) => setExerciseType(e.target.value)}
              >
                {EXERCISE_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Executed At"
              type="datetime-local"
              value={executedAt}
              onChange={(e) => setExecutedAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel id="exercise-ict-label" shrink>ICT Service</InputLabel>
              <Select
                labelId="exercise-ict-label"
                value={ictServiceId === '' ? '' : String(ictServiceId)}
                label="ICT Service"
                onChange={(e: SelectChangeEvent<string>) => setICTServiceId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {ictServices.map(ict => (
                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {ictServices.length === 0 && (
        <Alert severity="info">
          No ICT Services found. You can optionally create ICT services in the <strong>ICT Services</strong> panel to link them to testing exercises.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Testing Exercises</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedExercises.length === 0 ? (
            <Alert severity="info">No testing exercises yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Executed At</TableCell>
                    <TableCell>ICT Service</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedExercises.map(exercise => {
                    const isEditing = editingId === exercise.id;
                    const ictName = exercise.ict_service_id
                      ? (ictServices.find(ict => ict.id === exercise.ict_service_id)?.name || '—')
                      : 'None';
                    return (
                      <TableRow key={exercise.id ?? exercise.name}>
                        <TableCell width="22%">
                          {isEditing ? (
                            <TextField value={editName} onChange={(e) => setEditName(e.target.value)} size="small" fullWidth />
                          ) : exercise.name}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editExerciseType}
                                onChange={(e: SelectChangeEvent<string>) => setEditExerciseType(e.target.value)}
                              >
                                {EXERCISE_TYPES.map(type => (
                                  <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : exercise.exercise_type || '—'}
                        </TableCell>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField
                              type="datetime-local"
                              value={editExecutedAt}
                              onChange={(e) => setEditExecutedAt(e.target.value)}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : (exercise.executed_at ? new Date(exercise.executed_at).toLocaleString() : '—')}
                        </TableCell>
                        <TableCell width="18%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editICTServiceId === '' ? '' : String(editICTServiceId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditICTServiceId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {ictServices.map(ict => (
                                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : ictName}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(exercise)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(exercise)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(exercise.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TestingExercisesPanel;
